import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Building2, Mail, Lock, User, Target, TrendingUp, Shield, BarChart3, FileText, Phone } from 'lucide-react';
import { AlvoLogo } from '../components/AlvoLogo';

// Gradient colors matching the dashboard
const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const GRADIENT = `linear-gradient(135deg, ${PRIMARY_COLOR}, ${SECONDARY_COLOR})`;
const WHATSAPP_NUMBER = '5584999683171';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Preciso de ajuda com o ALVO.`;

// Premium Alvo Logo - uses the unified logo component
const AlvoLogoWithGlow = ({ size = 80, animated = true }) => (
  <div 
    className={`relative ${animated ? 'animate-fade-in' : ''}`} 
    style={{ width: size, height: size }}
  >
    <AlvoLogo size={size} withGlow={true} />
  </div>
);

// Floating geometric shapes for background - with gradient colors
const FloatingShapes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Large gradient orb - top right - using gradient colors */}
    <div 
      className="absolute w-[800px] h-[800px] rounded-full animate-float-slow"
      style={{
        background: `radial-gradient(circle, ${PRIMARY_COLOR}12 0%, transparent 60%)`,
        top: '-300px',
        right: '-300px',
        filter: 'blur(100px)'
      }}
    />
    {/* Secondary orb - bottom left - using secondary color */}
    <div 
      className="absolute w-[600px] h-[600px] rounded-full animate-float-reverse"
      style={{
        background: `radial-gradient(circle, ${SECONDARY_COLOR}10 0%, transparent 60%)`,
        bottom: '-200px',
        left: '-200px',
        filter: 'blur(80px)'
      }}
    />
    {/* Subtle grid pattern */}
    <div 
      className="absolute inset-0 opacity-[0.02]"
      style={{
        backgroundImage: `
          linear-gradient(${PRIMARY_COLOR}50 1px, transparent 1px),
          linear-gradient(90deg, ${PRIMARY_COLOR}50 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px'
      }}
    />
    {/* Corner accents with gradient */}
    <div 
      className="absolute top-0 left-0 w-40 h-40"
      style={{
        background: `linear-gradient(135deg, ${PRIMARY_COLOR}08 0%, transparent 50%)`,
        borderLeft: `1px solid ${PRIMARY_COLOR}15`,
        borderTop: `1px solid ${PRIMARY_COLOR}15`
      }}
    />
    <div 
      className="absolute bottom-0 right-0 w-40 h-40"
      style={{
        background: `linear-gradient(315deg, ${SECONDARY_COLOR}08 0%, transparent 50%)`,
        borderRight: `1px solid ${SECONDARY_COLOR}15`,
        borderBottom: `1px solid ${SECONDARY_COLOR}15`
      }}
    />
  </div>
);

// Feature badge component with gradient accent
const FeatureBadge = ({ icon: Icon, text, delay }) => (
  <div 
    className="flex items-center gap-2 px-3 py-1.5 rounded-full backdrop-blur-sm animate-fade-in-up"
    style={{ 
      animationDelay: delay,
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.06)'
    }}
  >
    <div 
      className="w-4 h-4 rounded-full flex items-center justify-center"
      style={{ background: GRADIENT }}
    >
      <Icon size={10} className="text-black" />
    </div>
    <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-medium">{text}</span>
  </div>
);

export const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [isLogin, setIsLogin] = useState(tabParam !== 'register');
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [pendingEmail, setPendingEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    gym_name: '',
    business_type: '',
    business_description: '',
    document_type: 'cpf',
    document_number: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
  });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  
  // Update isLogin when URL param changes
  useEffect(() => {
    if (tabParam === 'register') {
      setIsLogin(false);
    } else if (tabParam === 'login') {
      setIsLogin(true);
    }
  }, [tabParam]);

  // Business types for dropdown
  const businessTypes = [
    { value: 'academia', label: 'Academia / Fitness' },
    { value: 'varejo', label: 'Comércio / Varejo' },
    { value: 'servicos', label: 'Serviços Profissionais' },
    { value: 'restaurante', label: 'Restaurante / Alimentação' },
    { value: 'industria', label: 'Indústria' },
    { value: 'saude', label: 'Saúde / Clínicas' },
    { value: 'educacao', label: 'Educação' },
    { value: 'tecnologia', label: 'Tecnologia / Software' },
    { value: 'construcao', label: 'Construção Civil' },
    { value: 'logistica', label: 'Logística / Transporte' },
    { value: 'beleza', label: 'Beleza / Estética' },
    { value: 'consultoria', label: 'Consultoria' },
    { value: 'agronegocio', label: 'Agronegócio' },
    { value: 'outro', label: 'Outro' },
  ];

  // Brazilian states
  const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  // Format CPF: 000.000.000-00
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  // Format CNPJ: 00.000.000/0000-00
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 14);
    return numbers
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };

  // Format CEP: 00000-000
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 8);
    return numbers.replace(/(\d{5})(\d)/, '$1-$2');
  };

  const handleDocumentChange = (e) => {
    const value = e.target.value;
    const formatted = formData.document_type === 'cpf' ? formatCPF(value) : formatCNPJ(value);
    setFormData({ ...formData, document_number: formatted });
  };

  const handleZipCodeChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setFormData({ ...formData, zip_code: formatted });
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle verification code input
  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    pastedData.split('').forEach((char, i) => {
      if (i < 6) newCode[i] = char;
    });
    setVerificationCode(newCode);
  };

  const handleSendVerification = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erro ao enviar código');
      }
      
      setPendingEmail(formData.email);
      setShowVerification(true);
      toast.success('Código enviado para seu email!');
    } catch (error) {
      toast.error(error.message || 'Erro ao enviar código de verificação');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      toast.error('Digite o código completo');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingEmail, code })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Código inválido');
      }
      
      // Store token and user data
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      toast.success('Conta criada com sucesso!');
      navigate('/areas');
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Código inválido ou expirado');
      setVerificationCode(['', '', '', '', '', '']);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-verification?email=${encodeURIComponent(pendingEmail)}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erro ao reenviar código');
      }
      
      toast.success('Novo código enviado!');
      setVerificationCode(['', '', '', '', '', '']);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Login realizado com sucesso!');
        navigate('/areas');
      } else {
        // Register directly without email verification
        await register(formData);
        toast.success('Conta criada com sucesso!');
        navigate('/assinatura');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Verification Code Screen
  if (showVerification) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4 relative overflow-hidden">
        <FloatingShapes />
        
        <div className={`relative z-10 w-full max-w-[420px] transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <AlvoLogoWithGlow size={70} animated={true} />
            </div>
            <h1 
              className="text-3xl font-heading font-bold tracking-[0.3em] mb-2 uppercase bg-clip-text text-transparent"
              style={{ backgroundImage: GRADIENT }}
            >
              Verificação
            </h1>
          </div>

          {/* Card */}
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl opacity-50" style={{ background: `linear-gradient(135deg, ${PRIMARY_COLOR}05, ${SECONDARY_COLOR}05)`, filter: 'blur(40px)' }} />
            
            <div className="relative bg-gradient-to-br from-white/[0.03] to-white/[0.01] backdrop-blur-sm rounded-2xl p-8 border border-white/[0.04] shadow-2xl">
              
              {/* Email info */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: `linear-gradient(135deg, ${PRIMARY_COLOR}20, ${SECONDARY_COLOR}20)` }}>
                  <Mail className="text-[#22C55E]" size={28} />
                </div>
                <p className="text-zinc-400 text-sm mb-2">
                  Enviamos um código de 6 dígitos para
                </p>
                <p className="text-white font-medium">{pendingEmail}</p>
              </div>

              {/* Code Input */}
              <div className="flex justify-center gap-2 mb-6">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    onPaste={index === 0 ? handleCodePaste : undefined}
                    className="w-12 h-14 text-center text-2xl font-bold bg-black/40 border border-white/[0.08] text-white rounded-lg focus:border-[#22C55E]/50 focus:ring-2 focus:ring-[#22C55E]/20 transition-all"
                    style={{ caretColor: '#22C55E' }}
                  />
                ))}
              </div>

              {/* Verify Button */}
              <Button
                onClick={handleVerifyCode}
                disabled={loading || verificationCode.join('').length !== 6}
                className="w-full h-12 text-sm font-semibold uppercase tracking-wider rounded-lg transition-all duration-300 border-0"
                style={{ 
                  background: verificationCode.join('').length === 6 ? GRADIENT : '#333',
                  color: verificationCode.join('').length === 6 ? '#000' : '#666'
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Verificando...
                  </span>
                ) : (
                  'Verificar Código'
                )}
              </Button>

              {/* Resend */}
              <div className="text-center mt-6">
                <p className="text-zinc-500 text-sm mb-2">
                  Não recebeu o código?
                </p>
                <button
                  onClick={handleResendCode}
                  disabled={loading}
                  className="font-medium transition-colors bg-clip-text text-transparent hover:opacity-80"
                  style={{ backgroundImage: GRADIENT }}
                >
                  Reenviar código
                </button>
              </div>

              {/* Back button */}
              <div className="text-center mt-6 pt-6 border-t border-white/[0.04]">
                <button
                  onClick={() => {
                    setShowVerification(false);
                    setVerificationCode(['', '', '', '', '', '']);
                  }}
                  className="text-zinc-500 text-sm hover:text-zinc-300 transition-colors"
                >
                  ← Voltar para cadastro
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-zinc-400 text-[9px] uppercase tracking-[0.3em]">
              O código expira em 15 minutos
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background elements */}
      <FloatingShapes />
      
      {/* Main content container */}
      <div className={`relative z-10 w-full max-w-[420px] transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        
        {/* Logo section */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-6">
            <AlvoLogoWithGlow size={90} animated={mounted} />
          </div>
          
          {/* Brand name with refined typography */}
          <h1 
            className="text-4xl font-heading font-bold tracking-[0.35em] mb-3 uppercase bg-clip-text text-transparent"
            style={{ backgroundImage: GRADIENT }}
          >
            Alvo
          </h1>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-[1px] w-12" style={{ background: `linear-gradient(to right, transparent, ${PRIMARY_COLOR}30)` }} />
            <p className="text-zinc-500 text-[10px] uppercase tracking-[0.4em] font-medium">
              Performance Empresarial
            </p>
            <div className="h-[1px] w-12" style={{ background: `linear-gradient(to left, transparent, ${SECONDARY_COLOR}30)` }} />
          </div>
          
          {/* Feature badges */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <FeatureBadge icon={Target} text="Metas" delay="0.6s" />
            <FeatureBadge icon={BarChart3} text="Análises" delay="0.7s" />
            <FeatureBadge icon={TrendingUp} text="Performance" delay="0.8s" />
            <FeatureBadge icon={FileText} text="Relatórios" delay="0.9s" />
          </div>
        </div>

        {/* Form card - glassmorphism */}
        <div 
          className="rounded-2xl p-8 backdrop-blur-xl animate-fade-in-up"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.01) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow: '0 32px 64px -16px rgba(0, 0, 0, 0.6)',
            animationDelay: '0.3s'
          }}
        >
          {/* Tab switcher - minimal with gradient */}
          <div className="flex mb-8 p-1 rounded-lg bg-black/30 border border-white/[0.04]">
            <button
              type="button"
              data-testid="login-tab"
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2.5 px-4 rounded-md text-xs font-medium uppercase tracking-[0.15em] transition-all duration-300 ${
                isLogin 
                  ? 'text-black' 
                  : 'text-zinc-600 hover:text-zinc-400'
              }`}
              style={isLogin ? { background: GRADIENT } : {}}
            >
              Entrar
            </button>
            <button
              type="button"
              data-testid="register-tab"
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2.5 px-4 rounded-md text-xs font-medium uppercase tracking-[0.15em] transition-all duration-300 ${
                !isLogin 
                  ? 'text-black' 
                  : 'text-zinc-600 hover:text-zinc-400'
              }`}
              style={!isLogin ? { background: GRADIENT } : {}}
            >
              Criar Conta
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Registration fields */}
            {!isLogin && (
              <div className="space-y-5 animate-fade-in">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                    Seu Nome
                  </Label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-500 group-focus-within:text-[#22C55E]/70 transition-colors" size={16} />
                    <Input
                      id="name"
                      name="name"
                      data-testid="name-input"
                      type="text"
                      required={!isLogin}
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-11 h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                      placeholder="João Silva"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gym_name" className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                    Nome da Empresa
                  </Label>
                  <div className="relative group">
                    <Building2 className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-500 group-focus-within:text-[#22C55E]/70 transition-colors" size={16} />
                    <Input
                      id="gym_name"
                      name="gym_name"
                      data-testid="gym-name-input"
                      type="text"
                      required={!isLogin}
                      value={formData.gym_name}
                      onChange={handleChange}
                      className="pl-11 h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                      placeholder="Minha Empresa Ltda"
                    />
                  </div>
                </div>

                {/* Business Type Section */}
                <div className="space-y-2">
                  <Label htmlFor="business_type" className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                    Setor do Negócio *
                  </Label>
                  <select
                    id="business_type"
                    name="business_type"
                    data-testid="business-type-input"
                    required={!isLogin}
                    value={formData.business_type}
                    onChange={handleChange}
                    className="w-full h-12 px-4 bg-black/40 border border-white/[0.06] text-white text-sm rounded-lg transition-all focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 focus:outline-none"
                  >
                    <option value="" className="bg-zinc-900">Selecione o setor...</option>
                    {businessTypes.map(type => (
                      <option key={type.value} value={type.value} className="bg-zinc-900">{type.label}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-zinc-500">
                    O Diretor Financeiro usará benchmarks específicos do seu setor
                  </p>
                </div>

                {/* Business Description */}
                <div className="space-y-2">
                  <Label htmlFor="business_description" className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                    Sobre o Negócio
                  </Label>
                  <textarea
                    id="business_description"
                    name="business_description"
                    data-testid="business-description-input"
                    value={formData.business_description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-black/40 border border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all resize-none"
                    placeholder="Descreva seu negócio: produtos/serviços, público-alvo, diferenciais..."
                  />
                  <p className="text-[10px] text-zinc-500">
                    Quanto mais detalhes, melhor será a análise do Diretor Financeiro
                  </p>
                </div>

                {/* CPF/CNPJ Section */}
                <div className="space-y-2">
                  <Label className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                    CPF ou CNPJ
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex bg-black/40 border border-white/[0.06] rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, document_type: 'cpf', document_number: '' })}
                        className={`px-3 py-2 text-xs font-medium transition-all ${
                          formData.document_type === 'cpf' 
                            ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                            : 'text-zinc-500 hover:text-zinc-300'
                        }`}
                      >
                        CPF
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, document_type: 'cnpj', document_number: '' })}
                        className={`px-3 py-2 text-xs font-medium transition-all ${
                          formData.document_type === 'cnpj' 
                            ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                            : 'text-zinc-500 hover:text-zinc-300'
                        }`}
                      >
                        CNPJ
                      </button>
                    </div>
                    <Input
                      id="document_number"
                      name="document_number"
                      data-testid="document-number-input"
                      type="text"
                      value={formData.document_number}
                      onChange={handleDocumentChange}
                      className="flex-1 h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                      placeholder={formData.document_type === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
                    />
                  </div>
                </div>

                {/* Address Section */}
                <div className="space-y-3 pt-2 border-t border-white/[0.04]">
                  <Label className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                    Endereço
                  </Label>
                  
                  <Input
                    id="address"
                    name="address"
                    data-testid="address-input"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    className="h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                    placeholder="Rua, número, complemento"
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      id="city"
                      name="city"
                      data-testid="city-input"
                      type="text"
                      value={formData.city}
                      onChange={handleChange}
                      className="h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                      placeholder="Cidade"
                    />
                    <select
                      id="state"
                      name="state"
                      data-testid="state-input"
                      value={formData.state}
                      onChange={handleChange}
                      className="h-12 bg-black/40 border border-white/[0.06] text-white text-sm rounded-lg focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 transition-all px-3"
                    >
                      <option value="" className="bg-zinc-900">Estado</option>
                      {brazilianStates.map(state => (
                        <option key={state} value={state} className="bg-zinc-900">{state}</option>
                      ))}
                    </select>
                  </div>

                  <Input
                    id="zip_code"
                    name="zip_code"
                    data-testid="zip-code-input"
                    type="text"
                    value={formData.zip_code}
                    onChange={handleZipCodeChange}
                    className="h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                    placeholder="CEP: 00000-000"
                  />
                </div>
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                Email
              </Label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-500 group-focus-within:text-[#22C55E]/70 transition-colors" size={16} />
                <Input
                  id="email"
                  name="email"
                  data-testid="email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-11 h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">
                Senha
              </Label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-500 group-focus-within:text-[#22C55E]/70 transition-colors" size={16} />
                <Input
                  id="password"
                  name="password"
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-11 pr-11 h-12 bg-black/40 border-white/[0.06] text-white text-sm placeholder:text-zinc-500 focus:border-[#22C55E]/30 focus:ring-1 focus:ring-[#22C55E]/10 rounded-lg transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit button - premium with gradient */}
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full h-12 mt-6 text-black text-xs font-semibold uppercase tracking-[0.2em] rounded-lg transition-all duration-300 group disabled:opacity-50 hover:opacity-90"
              style={{ 
                background: GRADIENT,
                boxShadow: `0 10px 30px -10px ${PRIMARY_COLOR}40`
              }}
            >
              <span className="flex items-center justify-center gap-2">
                {loading ? (
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? 'Acessar' : 'Criar Conta'}
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={14} />
                  </>
                )}
              </span>
            </Button>
          </form>
          
          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-[1px]" style={{ background: `linear-gradient(to right, transparent, ${PRIMARY_COLOR}20)` }} />
            <span className="text-zinc-500 text-[9px] uppercase tracking-wider">ou</span>
            <div className="flex-1 h-[1px]" style={{ background: `linear-gradient(to left, transparent, ${SECONDARY_COLOR}20)` }} />
          </div>
          
          {/* Bottom text */}
          <p className="text-center text-zinc-400 text-[10px]">
            {isLogin ? 'Primeiro acesso?' : 'Já tem conta?'}{' '}
            <button 
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="font-medium transition-colors bg-clip-text text-transparent hover:opacity-80"
              style={{ backgroundImage: GRADIENT }}
            >
              {isLogin ? 'Crie sua conta' : 'Faça login'}
            </button>
          </p>
          
          {/* WhatsApp Contact */}
          <div className="mt-6 pt-6 border-t border-white/[0.06]">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-[#25D366] hover:text-[#20bd5a] transition-colors text-sm"
              data-testid="login-whatsapp-link"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span>Fale com a Alvo</span>
              <span className="text-zinc-500 text-xs">(84) 99968-3171</span>
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-zinc-400 text-[9px] uppercase tracking-[0.3em]">
            Sistema de Performance Empresarial
          </p>
        </div>
      </div>
    </div>
  );
};
